<template>
    <div class="page page--light">
        <section class="row gradient-background">
            <div class="wrapper">
                <loading-spinner v-if="loading" />
                <template v-else>
                    <header class="row__header">
                        <back-button :isAbsolute="false"
                            :isBlack="true"
                        />

                        <h1 id="leaderboard-details-title">{{ `${title}` }}</h1>
                    </header>

                    <div v-if="hasPrize"
                        class="block"
                    >
                        <div class="title-bar">
                            <h1>{{ $t('rewards.leaderboardDetails.prizes') }}</h1>
                            <a class="title-bar__right-link title-bar__right-link--bold"
                                @click="goToPrizeDetails"
                            >{{ $t('rewards.leaderboardDetails.moreDetails') }}</a>
                        </div>
                        <div class="prizes"
                            @click="goToPrizeDetails"
                        >
                            <div class="prizes__list">
                                <prize-item v-for="(prize, i) in sortedPrizesList.slice(0, 3)"
                                    :id="`leaderboard-prize-item-${i}`"
                                    :key="i"
                                    class="prizes__item"
                                    :image="getPrizeImage(prize)"
                                    :title="getTranslatedStr(prize, 'name', prize.name)"
                                    :order="prize.displayOrder"
                                    :quantity="prize.quantity"
                                />
                            </div>
                        </div>
                    </div>

                    <div class="block">
                        <div class="title-bar">
                            <h1>{{ $t('rewards.leaderboardTitle') }}</h1>
                            <span class="title-bar__right-link">{{ $t('rewards.leaderboardDetails.leaderboardHistory') }}</span>
                        </div>
                        <leaderboard-card ref="leaderboard"
                            class="leaderboard"
                            :leaderboardData="getSpecificLeaderboard"
                            :participants="getCurrentLeaderboardRankings"
                            :detailedView="true"
                            :searchable="true"
                        />
                    </div>
                </template>
            </div>
        </section>
    </div>
</template>

<script>
// 3rd party
import {
    mapGetters,
    mapActions,
} from 'vuex';

// Components
import backButton from '@webapp/components/back-button/back-button.vue';
import leaderboardCard from '@webapp/modules/rewards/components/leaderboards/leaderboard-card.vue';
import loadingSpinner from '@webapp/components/loading-spinner/loading-spinner.vue';
import PrizeItem from '@webapp/modules/rewards/components/leaderboards/prize-item.vue';

export default {
    name: 'leaderboard-details',
    components: {
        backButton,
        leaderboardCard,
        loadingSpinner,
        PrizeItem,
    },
    data: () => ({
        leaderboardsList: [],
        title: '',
        loading: true,
        analyticsSent: false,
    }),
    computed: {
        ...mapGetters('leaderboards', [
            'getSpecificLeaderboard',
            'getCurrentLeaderboardRankings',
            'sortPrizes',
        ]),

        ...mapGetters(['getTranslatedStr']),

        ...mapGetters('partners', ['partnerById']),

        hasPrize() {
            return this.getSpecificLeaderboard.leaderboardPrize.length > 0;
        },
        sortedPrizesList() {
            return this.sortPrizes(this.getSpecificLeaderboard.leaderboardPrize);
        },
    },
    watch: {
        getSpecificLeaderboard(leaderboard) {
            this.title = leaderboard.name;

            if (!this.analyticsSent) {
                const partner = this.getSpecificLeaderboard.partner && this.getSpecificLeaderboard.partner.id
                    ? this.partnerById(this.getSpecificLeaderboard.partner.id) : null;

                this.$webTrackingInstance?.sendEvent(`Leaderboards_${partner?.shortName || 'SSU'}`);
                this.analyticsSent = true;
            }
        },

        getCurrentLeaderboardRankings() {
            this.loading = false;

            // When the rankings properly load, scroll user into the leaderboard area
            if (this.$route.query.findUser) {
                const leaderboard = this.$refs.leaderboard;

                if (leaderboard) {
                    leaderboard.$el.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center',
                    });
                }
            }
        },
    },
    async created() {
        await this.fetchSpecificLeaderboard({
            leaderboardId: this.$route.params.id,
            itemsPerPage: 20,
        });
    },
    methods: {
        ...mapActions('leaderboards', ['fetchSpecificLeaderboard']),

        getPrizeImage(prize) {
            if (prize.prizeType?.images) {
                const image = prize.prizeType.images.find((imgObj) => imgObj.imageType.type === 'main');

                return image ? image.url : '';
            }

            return '';
        },

        goToPrizeDetails() {
            if (this.hasPrize) {
                this.$router.push({
                    name: 'prizeDetails',
                    path: `/rewards/leaderboards/prize-details/${this.getSpecificLeaderboard.id}`,
                });
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.gradient-background {
    padding: 2rem;
    background: linear-gradient(180deg, $white-color 0%, $athens-gray-color 35%);
}

.row {
    &__header {
        margin-bottom: 5.5rem;
    }
}

.prizes {
    background: $white-color;
    border-radius: 1rem;
    padding: 1rem 1.5rem;
    cursor: pointer;

    @include min-width(lg) {
        padding: 2rem 5rem;
    }

    &__list {
        display: flex;
        justify-content: space-between;
    }
}

.title-bar {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    margin-bottom: 0.9rem;

    h1 {
        font-size: 2rem;
        font-weight: 700;
        margin: 0;
    }

    &__right-link {
        font-size: 1.5rem;
        color: $lima-color;
        cursor: pointer;
        text-align: right;

        &--bold {
            font-weight: 700;
        }
    }
}

.block {
    margin-top: 5rem;
}
</style>